import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../../components/base/ConfirmModal';
import UserFormModal from './UserFormModal';
import {
  activatePlatformAdmin,
  createPlatformAdmin,
  deletePlatformAdmin,
  fetchPlatformAdmins,
  suspendPlatformAdmin,
  updatePlatformAdmin,
  type PlatformAdmin,
} from '../../../api/platformAdmins.api';
import { fetchPlatformRoles, type PlatformRole } from '../../../api/platformRoles.api';
import { usePlatformPermissions } from '../../../hooks/usePlatformPermissions';

type FilterKey = 'all' | 'active' | 'suspended';
type ConfirmType = 'delete' | 'suspend' | 'activate';

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((n) => n[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

const avatarColors = ['bg-teal-600', 'bg-emerald-600', 'bg-amber-500', 'bg-rose-500', 'bg-orange-500', 'bg-indigo-500'];

export default function UsersTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can, admin: me } = usePlatformPermissions();
  const [users, setUsers] = useState<PlatformAdmin[]>([]);
  const [roles, setRoles] = useState<PlatformRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [formModal, setFormModal] = useState<{ open: boolean; user: PlatformAdmin | null }>({ open: false, user: null });
  const [confirm, setConfirm] = useState<{ type: ConfirmType; userId: number } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const [adminsRes, rolesRes] = await Promise.all([fetchPlatformAdmins({ per_page: 100 }), fetchPlatformRoles()]);
    setLoading(false);
    if (adminsRes.ok === false) {
      if (adminsRes.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(adminsRes.message);
      setUsers([]);
      return;
    }
    setUsers(adminsRes.data);
    if (rolesRes.ok) setRoles(rolesRes.roles);
  }, [navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    let res = filter === 'all' ? users : users.filter((u) => u.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      res = res.filter(
        (u) =>
          u.name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          (u.role?.name ?? '').toLowerCase().includes(q),
      );
    }
    return res;
  }, [users, filter, search]);

  const counts = useMemo(
    () => ({
      total: users.length,
      active: users.filter((u) => u.status === 'active').length,
      suspended: users.filter((u) => u.status === 'suspended').length,
      roles: new Set(users.map((u) => u.platform_role_id).filter(Boolean)).size,
    }),
    [users],
  );

  const handleSave = async (data: {
    name: string;
    email: string;
    platform_role_id: number;
    status: string;
    password?: string;
  }) => {
    setBusy(true);
    setError('');
    if (formModal.user) {
      const r = await updatePlatformAdmin(formModal.user.id, {
        name: data.name,
        email: data.email,
        platform_role_id: data.platform_role_id,
        status: data.status,
        password: data.password || undefined,
      });
      setBusy(false);
      if (r.ok === false) {
        setError(r.message);
        return;
      }
    } else {
      if (!data.password) {
        setBusy(false);
        setError('كلمة المرور مطلوبة');
        return;
      }
      const r = await createPlatformAdmin({
        name: data.name,
        email: data.email,
        password: data.password,
        platform_role_id: data.platform_role_id,
        status: data.status,
      });
      setBusy(false);
      if (r.ok === false) {
        setError(r.message);
        return;
      }
    }
    setFormModal({ open: false, user: null });
    await load();
  };

  const runAction = async (type: ConfirmType, userId: number) => {
    setBusy(true);
    let r;
    if (type === 'delete') r = await deletePlatformAdmin(userId);
    else if (type === 'suspend') r = await suspendPlatformAdmin(userId);
    else r = await activatePlatformAdmin(userId);
    setBusy(false);
    setConfirm(null);
    if (r.ok === false) {
      setError(r.message);
      return;
    }
    await load();
  };

  return (
    <div className="flex flex-col gap-5">
      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      ) : null}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: t('users.summary.total', { defaultValue: 'الإجمالي' }), value: counts.total, icon: 'ri-team-line', bg: 'bg-teal-50 text-teal-600' },
          { label: t('users.summary.active', { defaultValue: 'نشط' }), value: counts.active, icon: 'ri-checkbox-circle-line', bg: 'bg-emerald-50 text-emerald-600' },
          { label: t('users.summary.suspended', { defaultValue: 'معلق' }), value: counts.suspended, icon: 'ri-forbid-line', bg: 'bg-rose-50 text-rose-500' },
          { label: t('users.summary.roles', { defaultValue: 'أدوار مستخدمة' }), value: counts.roles, icon: 'ri-shield-user-line', bg: 'bg-amber-50 text-amber-600' },
        ].map((c) => (
          <div key={c.label} className="bg-white rounded-xl p-4 ring-1 ring-gray-100 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${c.bg}`}>
              <i className={`${c.icon} text-lg`} />
            </div>
            <div>
              <p className="text-xs text-gray-400 font-medium">{c.label}</p>
              <p className="text-xl font-bold text-gray-900">{loading ? '…' : c.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl ring-1 ring-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            {([
              ['all', 'users.filter.all'],
              ['active', 'users.filter.active'],
              ['suspended', 'users.filter.suspended'],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                  filter === key ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {t(label, { defaultValue: key })}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('users.search_placeholder', { defaultValue: 'بحث…' })}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg w-52"
            />
            {can('add_user') ? (
              <button
                type="button"
                onClick={() => setFormModal({ open: true, user: null })}
                className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold"
              >
                {t('users.add_admin', { defaultValue: 'إضافة مسؤول' })}
              </button>
            ) : null}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/80 text-xs text-gray-500">
                <th className="px-4 py-3 text-start">{t('users.col.member', { defaultValue: 'العضو' })}</th>
                <th className="px-4 py-3 text-start">{t('users.col.role', { defaultValue: 'الدور' })}</th>
                <th className="px-4 py-3 text-start">{t('users.col.status', { defaultValue: 'الحالة' })}</th>
                <th className="px-4 py-3 text-start">{t('users.col.last_login', { defaultValue: 'آخر دخول' })}</th>
                <th className="px-4 py-3 text-start">{t('users.col.actions', { defaultValue: 'إجراءات' })}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">{t('users.no_users')}</td></tr>
              ) : (
                filtered.map((user, idx) => {
                  const isSelf = me?.id === user.id;
                  const isSuper = user.role?.slug === 'super-admin';
                  return (
                    <tr key={user.id} className="border-t border-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full text-white text-xs font-bold flex items-center justify-center ${avatarColors[idx % avatarColors.length]}`}>
                            {initials(user.name)}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-400">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-teal-50 text-teal-700">
                          {user.role?.name ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${user.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>
                          {t(`status.${user.status}`, { defaultValue: user.status })}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {user.last_login_at ? new Date(user.last_login_at).toLocaleString('ar-EG') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {can('edit_user') ? (
                            <button type="button" className="w-8 h-8 rounded-lg hover:bg-gray-100" onClick={() => setFormModal({ open: true, user })}>
                              <i className="ri-edit-line text-gray-500" />
                            </button>
                          ) : null}
                          {can('edit_user') && !isSelf ? (
                            <button
                              type="button"
                              className="w-8 h-8 rounded-lg hover:bg-gray-100"
                              onClick={() => setConfirm({ type: user.status === 'active' ? 'suspend' : 'activate', userId: user.id })}
                            >
                              <i className={`${user.status === 'active' ? 'ri-forbid-line text-amber-500' : 'ri-checkbox-circle-line text-emerald-600'}`} />
                            </button>
                          ) : null}
                          {can('delete_user') && !isSelf && !isSuper ? (
                            <button type="button" className="w-8 h-8 rounded-lg hover:bg-gray-100" onClick={() => setConfirm({ type: 'delete', userId: user.id })}>
                              <i className="ri-delete-bin-line text-rose-500" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <UserFormModal
        isOpen={formModal.open}
        user={formModal.user}
        roles={roles}
        saving={busy}
        onClose={() => setFormModal({ open: false, user: null })}
        onSave={handleSave}
      />

      <ConfirmModal
        isOpen={!!confirm}
        title={
          confirm?.type === 'delete'
            ? t('users.confirm_delete', { defaultValue: 'حذف المستخدم؟' })
            : confirm?.type === 'suspend'
              ? t('users.confirm_suspend', { defaultValue: 'تعليق المستخدم؟' })
              : t('users.confirm_activate', { defaultValue: 'تفعيل المستخدم؟' })
        }
        message={
          confirm?.type === 'delete'
            ? t('users.confirm_delete_msg', { defaultValue: 'سيتم حذف حساب المسؤول نهائياً.' })
            : confirm?.type === 'suspend'
              ? t('users.confirm_suspend_msg', { defaultValue: 'سيُمنع المستخدم من الدخول فوراً.' })
              : t('users.confirm_activate_msg', { defaultValue: 'سيتمكن المستخدم من الدخول مجدداً.' })
        }
        confirmVariant={confirm?.type === 'activate' ? 'success' : confirm?.type === 'suspend' ? 'warning' : 'danger'}
        onConfirm={() => {
          if (!confirm) return;
          void runAction(confirm.type, confirm.userId);
        }}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
}
